import { useCallback, useMemo } from 'react'

import {
  abrirSessao,
  ativar,
  digitar as digitarNoStore,
  encerrarSessao,
  ouvir as ouvirNoStore,
  redimensionar as redimensionarNoStore,
  useSsmStore,
  type EstadoDaAba,
  type SessaoNaTela,
} from '@/store/ssm'

import { lerFechamento, type LeituraDeFechamento } from './protocolo'

export type { EstadoDaAba }

export interface AbaDeServidor {
  instanceId: string
  nome: string
  ambiente: string
  estado: EstadoDaAba
  /** Preenchido quando a sessão fecha — diz o que fazer, não só o que houve. */
  motivo: LeituraDeFechamento | null
}

/**
 * Visão da tela de Servidores sobre as sessões.
 *
 * ## O que mudou em 04/09/2026 (TASK-10345)
 *
 * Este hook ERA o dono das sessões, e tinha um `useEffect` que encerrava todas ao
 * desmontar a tela. Agora ele é só um seletor sobre `@/store/ssm` — o dono é um
 * store de módulo, que sobrevive a trocar de rota. É o que torna possível o terminal
 * pinado continuar vivo fora de `/ssm` (TASK-10346).
 *
 * **O cleanup que encerrava tudo saiu daqui, e não sumiu:** ele foi substituído por
 * fusíveis do BROKER — ocioso de 30 min e duração de 8h (TASK-10343) e teto de
 * gravação (TASK-10348) — mais o encerramento no fim da sessão do console, que mora
 * na inscrição do store (`store/ssm.ts`, final do arquivo). Fusível no desmonte de
 * componente não serve para uma sessão que precisa sobreviver ao desmonte.
 *
 * VÁRIAS SESSÕES VIVAS AO MESMO TEMPO, e trocar de aba NÃO fecha a anterior —
 * decisão do Carlos em 31/08/2026. É o que faz o teto de sessões importar.
 */
export function useSessoesSsm(tenantId: number | undefined, tenantNome = '') {
  const sessoes = useSsmStore(e => e.sessoes)
  const ativa = useSsmStore(e => e.ativa)
  const historico = useSsmStore(e => e.historico)

  // A tela desenha só as que estão em modo de aba: uma sessão pinada é desenhada
  // pela janela flutuante, e renderizar as duas faria dois terminais disputarem a
  // mesma sessão.
  const abas = useMemo<AbaDeServidor[]>(
    () =>
      Object.values(sessoes)
        .filter((s: SessaoNaTela) => s.modo === 'aba')
        .map(({ instanceId, nome, ambiente, estado, motivo }) => ({
          instanceId, nome, ambiente, estado, motivo,
        })),
    [sessoes],
  )

  const pinadas = useMemo(
    () => Object.values(sessoes).filter((s: SessaoNaTela) => s.modo === 'flutuante'),
    [sessoes],
  )

  const abrir = useCallback(
    (instanceId: string, nome: string, ambiente: string) => {
      if (!tenantId) return
      void abrirSessao({ instanceId, nome, ambiente, tenantId, tenantNome })
    },
    [tenantId, tenantNome],
  )

  const ouvir = useCallback(
    (instanceId: string, fn: (d: Uint8Array) => void) => ouvirNoStore(instanceId, fn),
    [],
  )

  return {
    abas,
    pinadas,
    ativa,
    setAtiva: ativar,
    abrir,
    fechar: encerrarSessao,
    digitar: digitarNoStore,
    redimensionar: redimensionarNoStore,
    historico,
    ouvir,
  }
}

export { lerFechamento }
